import React, { useState, useRef, useEffect } from 'react';
import { FileText, Loader2, AlertCircle, Search, ChevronLeft, ChevronRight, ChevronDown, Plus, Edit, Trash2, Info } from 'lucide-react';
import { useSelector } from 'react-redux';
import { 
  useListQuestionBankQuestionsQuery,
  useGetQuestionBankFilterValuesQuery,
  useCreateQuestionBankQuestionMutation,
  useUpdateQuestionBankQuestionMutation,
  useDeleteQuestionBankQuestionMutation,
  type Question,
  type QuestionBankCreate
} from '../../redux/api/qaApi';
import { useDebounce } from '../../utils/debounce';
import CreateQuestionModal from '../modals/CreateQuestionModal';
import type { RootState } from '../../redux/store';

// Helper function to format display text (remove underscores, capitalize words)
const formatDisplayText = (text: string): string => {
  if (!text) return '';
  return text
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Format question scope with alias: "immediate" → "Per Page"
const formatQuestionScope = (text: string): string => {
  if (!text) return '';
  if (text.toLowerCase() === 'immediate') return 'Per Page';
  return formatDisplayText(text);
};

const HeaderTooltip: React.FC<{ text: string; align?: 'left' | 'right' }> = ({ text, align = 'left' }) => (
  <span className="relative group/tip ml-1 inline-flex align-middle">
    <Info className="w-3.5 h-3.5 text-[var(--color-text-muted)] cursor-help" />
    <span className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full mt-2 w-64 px-3 py-2 rounded-lg bg-[var(--color-surface-raised)] border border-[var(--color-border)] text-xs font-normal text-[var(--color-text-secondary)] leading-relaxed shadow-lg opacity-0 pointer-events-none group-hover/tip:opacity-100 group-hover/tip:pointer-events-auto transition-opacity z-50 normal-case tracking-normal whitespace-normal`}>
      {text}
    </span>
  </span>
);

// Helper function to get URL search params
const getUrlParams = (): URLSearchParams => {
  return new URLSearchParams(window.location.search);
};

// Helper function to update URL without reload
const updateUrlParams = (params: Record<string, string | number | null | undefined>) => {
  const urlParams = getUrlParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      urlParams.delete(key);
    } else {
      urlParams.set(key, String(value));
    }
  });

  const newUrl = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;
  window.history.replaceState({}, '', newUrl);
};

// Helper function to get initial state from URL params
const getInitialStateFromUrl = () => {
  const params = getUrlParams();
  const pageParam = params.get('page');
  const limitParam = params.get('limit');
  
  // Parse page - if invalid or missing, default to 1
  let page = 1;
  if (pageParam) {
    const parsed = parseInt(pageParam, 10);
    if (!isNaN(parsed) && parsed > 0) {
      page = parsed;
    }
  }
  
  // Parse limit - if invalid or missing, default to 20
  let limit = 20;
  if (limitParam) {
    const parsed = parseInt(limitParam, 10);
    if (!isNaN(parsed) && parsed > 0) {
      limit = parsed;
    }
  }
  
  return {
    page,
    limit,
    category: params.get('category') || '',
    qc_type: params.get('qc_type') || '',
    search: params.get('search') || '',
  };
};

const QuestionSetsTab: React.FC = () => {
  const selectedOrg = useSelector((state: RootState) => state.organization.currentOrganization);
  const tenantId = selectedOrg?.id;
  const currentSite = useSelector((state: RootState) => state.site.currentSite);
  const siteId = currentSite?.site_id;

  // Initialize state from URL params - read synchronously on mount
  // Read URL params directly to ensure we get the current URL, not stale values
  const initialState = React.useMemo(() => getInitialStateFromUrl(), []);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(initialState.page);
  const [itemsPerPage, setItemsPerPage] = useState<number>(initialState.limit);
  const [offset, setOffset] = useState<number>((initialState.page - 1) * initialState.limit);

  // Filter and search state
  const [searchQuery, setSearchQuery] = useState<string>(initialState.search);
  const [categoryFilter, setCategoryFilter] = useState<string>(initialState.category);
  const [qcTypeFilter, setQcTypeFilter] = useState<string>(initialState.qc_type);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [qcTypeDropdownOpen, setQcTypeDropdownOpen] = useState(false);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const qcTypeDropdownRef = useRef<HTMLDivElement>(null);

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Track previous state values to detect actual changes (not just re-renders)
  const prevState = useRef({
    page: currentPage,
    limit: itemsPerPage,
    category: categoryFilter,
    qc_type: qcTypeFilter,
    search: searchQuery,
  });
  
  // Track if this is the initial mount to prevent overwriting URL on first render
  const isInitialMount = useRef(true);

  // Update URL params when state changes (but not on initial mount to preserve URL params)
  useEffect(() => {
    // Skip URL update on initial mount - preserve whatever URL the user navigated to
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // Update prevState to current state after initial mount
      prevState.current = {
        page: currentPage,
        limit: itemsPerPage,
        category: categoryFilter,
        qc_type: qcTypeFilter,
        search: searchQuery,
      };
      return;
    }
    
    // Only update URL if state actually changed (not just a re-render)
    const stateChanged = 
      prevState.current.page !== currentPage ||
      prevState.current.limit !== itemsPerPage ||
      prevState.current.category !== categoryFilter ||
      prevState.current.qc_type !== qcTypeFilter ||
      prevState.current.search !== searchQuery;
    
    if (!stateChanged) {
      return;
    }
    
    // Update prevState before updating URL
    prevState.current = {
      page: currentPage,
      limit: itemsPerPage,
      category: categoryFilter,
      qc_type: qcTypeFilter,
      search: searchQuery,
    };
    
    updateUrlParams({
      page: currentPage,
      limit: itemsPerPage,
      category: categoryFilter || null,
      qc_type: qcTypeFilter || null,
      search: searchQuery || null,
    });
  }, [currentPage, itemsPerPage, categoryFilter, qcTypeFilter, searchQuery]);

  // Listen for browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const newState = getInitialStateFromUrl();
      setCurrentPage(newState.page);
      setItemsPerPage(newState.limit);
      setOffset((newState.page - 1) * newState.limit);
      setCategoryFilter(newState.category);
      setQcTypeFilter(newState.qc_type);
      setSearchQuery(newState.search);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!categoryDropdownRef.current?.contains(target)) setCategoryDropdownOpen(false);
      if (!qcTypeDropdownRef.current?.contains(target)) setQcTypeDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [deleteConfirmQuestion, setDeleteConfirmQuestion] = useState<Question | null>(null);
  
  // Tooltip state - track which question's tooltip is open
  const [openTooltipQuestionId, setOpenTooltipQuestionId] = useState<string | null>(null);

  // Fetch questions with filters
  const { data: questionsData, isLoading, error, refetch } = useListQuestionBankQuestionsQuery({
    limit: itemsPerPage,
    offset: offset,
    category: categoryFilter || undefined,
    qc_type: qcTypeFilter || undefined,
    tenant_id: tenantId || undefined,
  });

  // Fetch filter values
  const { data: filterValues } = useGetQuestionBankFilterValuesQuery({ tenant_id: tenantId || undefined });

  // Create question mutation
  const [createQuestion, { isLoading: isCreating }] = useCreateQuestionBankQuestionMutation();
  
  // Update question mutation
  const [updateQuestion, { isLoading: isUpdating }] = useUpdateQuestionBankQuestionMutation();
  
  // Delete question mutation
  const [deleteQuestion, { isLoading: isDeleting }] = useDeleteQuestionBankQuestionMutation();

  // Filter questions by search query (client-side on current page)
  const filteredQuestions = React.useMemo(() => {
    if (!questionsData?.items) return [];
    
    let filtered = questionsData.items;
    
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(q => 
        q.check_description.toLowerCase().includes(query) ||
        q.category.toLowerCase().includes(query) ||
        q.qc_type.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [questionsData?.items, debouncedSearchQuery]);

  // Calculate pagination (use API total, but adjust for client-side search)
  const totalItems = questionsData?.total || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = offset + 1;
  const endItem = Math.min(offset + itemsPerPage, totalItems);
  
  // Reset to first page when filters change (but not on initial mount or when initialized from URL)
  const initialFilters = useRef({
    category: initialState.category,
    qc_type: initialState.qc_type,
  });
  const isFilterInitialMount = useRef(true);
  
  React.useEffect(() => {
    if (isFilterInitialMount.current) {
      isFilterInitialMount.current = false;
      // Store initial filter values and don't reset on initial mount
      return;
    }
    
    // Only reset page if filters actually changed from initial values
    const filtersChanged = 
      categoryFilter !== initialFilters.current.category ||
      qcTypeFilter !== initialFilters.current.qc_type;
    
    if (filtersChanged) {
      setOffset(0);
      setCurrentPage(1);
      // Update initial filters to new values
      initialFilters.current = {
        category: categoryFilter,
        qc_type: qcTypeFilter,
      };
    }
  }, [categoryFilter, qcTypeFilter]);

  // Pagination handlers
  const handleNextPage = () => {
    if (offset + itemsPerPage < totalItems) {
      const newPage = currentPage + 1;
      const newOffset = (newPage - 1) * itemsPerPage;
      setOffset(newOffset);
      setCurrentPage(newPage);
    }
  };

  const handlePreviousPage = () => {
    if (offset > 0) {
      const newPage = Math.max(1, currentPage - 1);
      const newOffset = (newPage - 1) * itemsPerPage;
      setOffset(newOffset);
      setCurrentPage(newPage);
    }
  };

  const handlePageChange = (page: number) => {
    const newOffset = (page - 1) * itemsPerPage;
    setOffset(newOffset);
    setCurrentPage(page);
  };

  const handleLimitChange = (newLimit: number) => {
    setItemsPerPage(newLimit);
    const newPage = 1;
    setCurrentPage(newPage);
    setOffset(0);
  };

  // Get unique categories and qc_types for filters
  const uniqueCategories = filterValues?.categories || [];
  const uniqueQcTypes = filterValues?.qc_types || [];

  const handleCreateQuestion = async (data: QuestionBankCreate) => {
    try {
      await createQuestion({ ...data, tenant_id: tenantId || undefined }).unwrap();
      setIsCreateModalOpen(false);
      // Refetch questions to show the new one
      refetch();
    } catch (error) {
      console.error('Failed to create question:', error);
      alert(`Failed to create question: ${error && 'data' in error ? JSON.stringify(error.data) : 'Unknown error'}`);
    }
  };

  const handleEditQuestion = (question: Question) => {
    setEditingQuestion(question);
  };

  const handleUpdateQuestion = async (data: QuestionBankCreate) => {
    if (!editingQuestion) return;
    try {
      await updateQuestion({ question_id: editingQuestion.id, body: data, tenant_id: tenantId || undefined }).unwrap();
      setEditingQuestion(null);
      // Refetch questions to show the updated one
      refetch();
    } catch (error) {
      console.error('Failed to update question:', error);
      alert(`Failed to update question: ${error && 'data' in error ? JSON.stringify(error.data) : 'Unknown error'}`);
    }
  };

  const handleDeleteQuestion = async () => {
    if (!deleteConfirmQuestion) return;
    try {
      await deleteQuestion({ question_id: deleteConfirmQuestion.id, tenant_id: tenantId || undefined }).unwrap();
      setDeleteConfirmQuestion(null);
      // Refetch questions to remove the deleted one
      refetch();
    } catch (error) {
      console.error('Failed to delete question:', error);
      alert(`Failed to delete question: ${error && 'data' in error ? JSON.stringify(error.data) : 'Unknown error'}`);
    }
  };

  return (
    <div className="eg-page-shell">
      {/* Header */}
      <div className="eg-toolbar-header p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold eg-text-primary mb-1">Question Set</h2>
            <p className="text-sm eg-text-secondary">
              {totalItems} questions
            </p>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="px-4 py-2 bg-engramiq-green hover:bg-engramiq-green/80 rounded-lg text-engramiq-dark-blue transition-all flex items-center gap-2 font-medium"
          >
            <Plus className="w-4 h-4" />
            Add New Question
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 eg-text-secondary" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search questions..."
              className="w-full pl-10 pr-4 py-2 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded-lg eg-text-primary placeholder-[var(--color-text-secondary)] focus:outline-none focus:border-engramiq-green"
            />
          </div>

          {/* Category Filter - custom dropdown like Sidebar */}
          <div className="relative min-w-[200px]" ref={categoryDropdownRef}>
            <button
              type="button"
              onClick={() => {
                setCategoryDropdownOpen((o) => !o);
                setQcTypeDropdownOpen(false);
              }}
              className="w-full px-3 py-2 pr-8 bg-[var(--color-input-bg)] hover:bg-engramiq-green/20 rounded-lg eg-text-primary text-sm text-left focus:outline-none transition-all truncate"
            >
              {categoryFilter ? formatDisplayText(categoryFilter) : 'All Categories'}
            </button>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-engramiq-green pointer-events-none" />
            {categoryDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--color-bg-primary)] rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto flex flex-col border border-[var(--color-border)]">
                  <button
                    type="button"
                    onClick={() => {
                      setCategoryFilter('');
                      setCategoryDropdownOpen(false);
                    }}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-surface-hover)] transition-colors ${
                    !categoryFilter ? 'bg-engramiq-green/20 eg-text-primary' : 'eg-text-primary'
                  }`}
                >
                  All Categories
                </button>
                {uniqueCategories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => {
                      setCategoryFilter(cat);
                      setCategoryDropdownOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-surface-hover)] transition-colors ${
                      categoryFilter === cat ? 'bg-engramiq-green/20 eg-text-primary' : 'eg-text-primary'
                    }`}
                  >
                    {formatDisplayText(cat)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* QC Type Filter - custom dropdown like Sidebar */}
          <div className="relative min-w-[180px]" ref={qcTypeDropdownRef}>
            <button
              type="button"
              onClick={() => {
                setQcTypeDropdownOpen((o) => !o);
                setCategoryDropdownOpen(false);
              }}
              className="w-full px-3 py-2 pr-8 bg-[var(--color-input-bg)] hover:bg-engramiq-green/20 rounded-lg eg-text-primary text-sm text-left focus:outline-none transition-all truncate"
            >
              {qcTypeFilter ? formatDisplayText(qcTypeFilter) : 'All QC Types'}
            </button>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-engramiq-green pointer-events-none" />
            {qcTypeDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--color-bg-primary)] rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto flex flex-col border border-[var(--color-border)]">
                  <button
                    type="button"
                    onClick={() => {
                      setQcTypeFilter('');
                      setQcTypeDropdownOpen(false);
                    }}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-surface-hover)] transition-colors ${
                    !qcTypeFilter ? 'bg-engramiq-green/20 eg-text-primary' : 'eg-text-primary'
                  }`}
                >
                  All QC Types
                </button>
                {uniqueQcTypes.map((qcType) => (
                  <button
                    key={qcType}
                    type="button"
                    onClick={() => {
                      setQcTypeFilter(qcType);
                      setQcTypeDropdownOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-surface-hover)] transition-colors ${
                      qcTypeFilter === qcType ? 'bg-engramiq-green/20 eg-text-primary' : 'eg-text-primary'
                    }`}
                  >
                    {formatDisplayText(qcType)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="eg-page-content">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-engramiq-green" />
            <span className="ml-3 eg-text-primary">Loading questions...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <AlertCircle className="w-8 h-8 text-accent-red mr-3" />
            <div className="eg-text-primary">
              <p className="font-semibold">Error loading questions</p>
              <p className="text-sm eg-text-secondary mt-1">
                {error && 'data' in error ? JSON.stringify(error.data) : 'Unknown error'}
              </p>
            </div>
          </div>
        ) : !filteredQuestions || filteredQuestions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <FileText className="w-16 h-16 eg-text-secondary mb-4" />
            <p className="text-lg eg-text-primary mb-2">No Questions Found</p>
            <p className="text-sm eg-text-secondary">
              {searchQuery || categoryFilter || qcTypeFilter 
                ? 'Try adjusting your filters' 
                : 'No questions available'}
            </p>
          </div>
        ) : (
          <div className="p-4">
            {/* Table */}
            <div className="eg-card-elevated overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead style={{ borderBottom: '1px solid var(--color-overlay-light)' }}>
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <span className="text-sm font-semibold eg-text-primary inline-flex items-center">
                          Question
                          <HeaderTooltip text="The question or check the agent will evaluate on each applicable page." />
                        </span>
                      </th>
                      <th className="px-4 py-3 text-left whitespace-nowrap">
                        <span className="text-sm font-semibold eg-text-primary inline-flex items-center">
                          QC Type
                          <HeaderTooltip text="Classification of the check: Drawing QC (visual/formatting) vs Design QC (engineering correctness)." />
                        </span>
                      </th>
                      <th className="px-4 py-3 text-left">
                        <span className="text-sm font-semibold eg-text-primary inline-flex items-center">
                          Category
                          <HeaderTooltip text="Grouping label for organizing related checks (e.g. Annotations, Titleblock)." />
                        </span>
                      </th>
                      <th className="px-4 py-3 text-left">
                        <span className="text-sm font-semibold eg-text-primary inline-flex items-center">
                          Applicable Page Types
                          <HeaderTooltip text="Sheet types this question will be asked on. Each page matching these types gets assigned this question." />
                        </span>
                      </th>
                      <th className="px-4 py-3 text-left whitespace-nowrap">
                        <span className="text-sm font-semibold eg-text-primary inline-flex items-center">
                          Question Scope
                          <HeaderTooltip text="Per Page = can be answered from a single page. Cross Sheet = requires comparing multiple sheet types." align="right" />
                        </span>
                      </th>
                      <th className="px-4 py-3 text-right">
                        <span className="text-sm font-semibold eg-text-primary">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-border)]">
                    {filteredQuestions.map((question) => (
                      <tr
                        key={question.id}
                        className="hover:bg-[var(--color-surface-hover)] transition-colors"
                      >
                        <td className="px-4 py-3 text-sm eg-text-primary">
                          {question.check_description}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="px-2 py-1 bg-engramiq-green/20 text-engramiq-green rounded text-xs">
                            {formatDisplayText(question.qc_type)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-blue-400/20 text-blue-400 rounded text-xs">
                            {formatDisplayText(question.category)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {question.applicable_sheet_types && question.applicable_sheet_types.length > 0 ? (
                            <div className="flex flex-wrap gap-1 items-center">
                              {(() => {
                                const sheetTypes = question.applicable_sheet_types;
                                const maxVisible = 4;
                                const shouldTruncate = sheetTypes.length > 5;
                                const visibleTypes = shouldTruncate ? sheetTypes.slice(0, maxVisible) : sheetTypes;
                                const remainingCount = shouldTruncate ? sheetTypes.length - maxVisible : 0;
                                const remainingTypes = shouldTruncate ? sheetTypes.slice(maxVisible) : [];

                                return (
                                  <>
                                    {visibleTypes.map((sheetType, idx) => (
                                      <span
                                        key={`${question.id}-sheet-${idx}-${sheetType}`}
                                        className="px-2 py-0.5 bg-purple-400/20 text-purple-400 rounded text-xs border border-purple-400/30"
                                      >
                                        {formatDisplayText(sheetType)}
                                      </span>
                                    ))}
                                    {shouldTruncate && (
                                      <span className="relative inline-block">
                                        <span 
                                          className="px-2 py-0.5 bg-engramiq-bright-grey/20 eg-text-secondary rounded text-xs border border-engramiq-bright-grey/30 cursor-help"
                                          onMouseEnter={() => setOpenTooltipQuestionId(question.id)}
                                          onMouseLeave={() => setOpenTooltipQuestionId(null)}
                                        >
                                          +{remainingCount} more
                                        </span>
                                        {/* Tooltip */}
                                        {openTooltipQuestionId === question.id && (
                                          <div 
                                            className={`absolute top-full mt-1 z-[100] ${
                                              remainingCount > 10 ? 'right-0' : 'left-0'
                                            }`}
                                            onMouseEnter={() => setOpenTooltipQuestionId(question.id)}
                                            onMouseLeave={() => setOpenTooltipQuestionId(null)}
                                          >
                                            <div className={`bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-lg p-3 shadow-lg ${
                                              remainingCount > 10 
                                                ? 'min-w-[400px] max-w-[500px]' 
                                                : 'min-w-[200px] max-w-xs'
                                            }`}>
                                              <div className="text-xs font-semibold eg-text-primary mb-2">
                                                Remaining Types ({remainingCount}):
                                              </div>
                                              <div className="flex flex-wrap gap-1 max-h-[300px] overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-engramiq-green/30 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-track]:bg-[var(--color-surface)]">
                                                {remainingTypes.map((sheetType, idx) => (
                                                  <span
                                                    key={`${question.id}-tooltip-${idx}-${sheetType}`}
                                                    className="px-2 py-0.5 bg-purple-400/20 text-purple-400 rounded text-xs border border-purple-400/30 whitespace-nowrap"
                                                  >
                                                    {formatDisplayText(sheetType)}
                                                  </span>
                                                ))}
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </span>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          ) : (
                            <span className="text-xs eg-text-secondary">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="px-2 py-1 bg-orange-400/20 text-orange-400 rounded text-xs border border-orange-400/30">
                            {formatQuestionScope(question.validation_trigger)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEditQuestion(question)}
                              className="p-1.5 hover:bg-[var(--color-surface-hover)] rounded transition-colors text-engramiq-green hover:text-engramiq-green/80"
                              title="Edit question"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirmQuestion(question)}
                              className="p-1.5 hover:bg-[var(--color-surface-hover)] rounded transition-colors text-red-400 hover:text-red-300"
                              title="Delete question"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="text-sm eg-text-secondary">
                    Showing {startItem} to {endItem} of {totalItems} questions
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm eg-text-secondary">Items per page:</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => handleLimitChange(Number(e.target.value))}
                      className="px-2 py-1 bg-[var(--color-input-bg)] border border-[var(--color-border)] rounded eg-text-primary text-sm focus:outline-none focus:border-engramiq-green"
                    >
                      {(() => {
                        const standardOptions = [10, 20, 50, 100];
                        const options = [...standardOptions];
                        // Add current value if it's not in the standard options
                        if (!standardOptions.includes(itemsPerPage)) {
                          options.push(itemsPerPage);
                          options.sort((a, b) => a - b);
                        }
                        return options.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ));
                      })()}
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={offset === 0 || isLoading}
                    className="px-3 py-1.5 bg-[var(--color-bg-primary)] hover:bg-[var(--color-surface-hover)] border border-[var(--color-border)] hover:border-engramiq-green rounded-lg text-engramiq-green transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--color-surface)] disabled:hover:border-[var(--color-border)] flex items-center gap-1"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span>Previous</span>
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          disabled={isLoading}
                          className={`px-3 py-1.5 rounded-lg transition-all ${
                            currentPage === pageNum
                              ? 'bg-engramiq-green text-engramiq-dark-blue font-medium'
                              : 'bg-[var(--color-bg-primary)] hover:bg-[var(--color-surface-hover)] eg-text-primary border border-[var(--color-border)] hover:border-engramiq-green'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={offset + itemsPerPage >= totalItems || isLoading}
                    className="px-3 py-1.5 bg-[var(--color-bg-primary)] hover:bg-[var(--color-surface-hover)] border border-[var(--color-border)] hover:border-engramiq-green rounded-lg text-engramiq-green transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--color-surface)] disabled:hover:border-[var(--color-border)] flex items-center gap-1"
                  >
                    <span>Next</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Question Modal */}
      <CreateQuestionModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateQuestion}
        isLoading={isCreating}
        tenantId={tenantId}
      />

      {/* Edit Question Modal */}
      <CreateQuestionModal
        isOpen={!!editingQuestion}
        onClose={() => setEditingQuestion(null)}
        onSubmit={handleUpdateQuestion}
        isLoading={isUpdating}
        question={editingQuestion}
        tenantId={tenantId}
      />

      {/* Delete Confirmation Dialog */}
      {deleteConfirmQuestion && (
        <div className="fixed inset-0 flex items-center justify-center z-[100000]" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold eg-text-primary mb-2">Delete Question</h3>
            <p className="text-sm eg-text-secondary mb-4">
              Are you sure you want to delete this question? This action cannot be undone.
            </p>
            <div className="bg-[var(--color-surface)] rounded p-3 mb-4">
              <p className="text-sm eg-text-primary">{deleteConfirmQuestion.check_description}</p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmQuestion(null)}
                disabled={isDeleting}
                className="px-4 py-2 bg-[var(--color-bg-primary)] border border-[var(--color-border)] hover:border-engramiq-green rounded-lg eg-text-primary transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteQuestion}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionSetsTab;
